import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { generatePath } from 'react-router-dom';
import { API_URLS } from '../../../routes/urls';
import { convertObjectToFormData } from '../../../utils/form';
import { updateGuestUserEmail } from '../../../utils/qualification';
import { apiSlice } from '../../apiSlice';
import { QueryErrorReturnValue } from '../../types';
import { addSnackbar } from '../snackbar/snackbarSlice';
import { userApi } from '../user/userSlice';
import {
  QualificationByEmailResponse,
  QualificationOptions,
  QualificationState,
  QualificationUpsertInputs,
  UpsertQualificationResponse,
} from './types';

const initialQualificationUIState: QualificationState = {
  isQualificationDrawerOpen: false,
  guestUser: null,
  guestQualification: null,
  isBackButtonVisible: false,
  useCriteriaFrom: null,
  selectedModule: null,
};

const qualificationUISlice = createSlice({
  name: 'qualification',
  initialState: initialQualificationUIState,
  reducers: {
    openQualificationDrawer(state, action?: PayloadAction<QualificationState['selectedModule'] | undefined>) {
      state.isQualificationDrawerOpen = true;
      if (action?.payload) {
        state.selectedModule = action.payload;
      }
    },
    closeQualificationDrawer(state) {
      state.isQualificationDrawerOpen = false;
      state.isBackButtonVisible = false;
      state.selectedModule = null;
    },
    updateGuestUser(state, action: PayloadAction<QualificationState['guestUser']>) {
      updateGuestUserEmail(action.payload?.email);
      state.guestUser = action.payload;
      if (!action.payload) {
        state.guestQualification = null;
        state.useCriteriaFrom = null;
      } else {
        state.useCriteriaFrom = 'qualification';
      }
    },
    updateGuestQualification(state, action: PayloadAction<QualificationState['guestQualification']>) {
      state.guestQualification = action.payload;
    },
    showBackButton(state) {
      state.isBackButtonVisible = true;
    },
    hideBackButton(state) {
      state.isBackButtonVisible = false;
    },
    updateUsedCriteriaFrom(state, action: PayloadAction<QualificationState['useCriteriaFrom']>) {
      state.useCriteriaFrom = action.payload;
    },
  },
});

export const {
  closeQualificationDrawer,
  openQualificationDrawer,
  updateGuestUser,
  updateGuestQualification,
  showBackButton,
  hideBackButton,
  updateUsedCriteriaFrom,
} = qualificationUISlice.actions;
export const qualificationUIReducer = qualificationUISlice.reducer;

const qualificationApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    qualificationOptions: builder.query<QualificationOptions, void>({
      query: () => ({
        url: API_URLS.qualificationOptions,
      }),
      transformResponse: (response: QualificationOptions) => {
        response.education_levels = response.education_levels.reduce<QualificationOptions['education_levels']>(
          (prevEducationLevels, educationLevel) => {
            const existingItem = prevEducationLevels.find((level) => level.value === educationLevel.value);
            if (existingItem) {
              existingItem.label += ` / ${educationLevel.label}`;
            } else {
              prevEducationLevels.push(educationLevel);
            }
            return prevEducationLevels;
          },
          []
        );
        return response;
      },
    }),
    qualificationByEmail: builder.query<QualificationByEmailResponse, string>({
      query: (email) => ({
        url: generatePath(API_URLS.qualificationByEmail, { email }),
      }),
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        const { data } = await queryFulfilled;
        if (data.userType === 'guest') {
          dispatch(updateGuestQualification(data.qualification || null));
          dispatch(updateGuestUser(data.user));
        }
      },
    }),
    upsertQualification: builder.mutation<UpsertQualificationResponse, QualificationUpsertInputs>({
      query: (payload) => ({
        url: API_URLS.upsertQualification,
        method: 'POST',
        data: convertObjectToFormData(payload),
      }),
      onQueryStarted: async (_payload, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          if (data.userType === 'guest') {
            dispatch(updateGuestUser(data.user));
            dispatch(updateGuestQualification(data.qualification));
          } else {
            dispatch(
              userApi.util.updateQueryData(
                'getProfile',
                { slug: data.user.slug, userId: data.user.id.toString() },
                (draft) => {
                  if (draft.user) {
                    draft.user.qualification = data.qualification;
                  }
                }
              )
            );
          }
          dispatch(
            addSnackbar({
              message: 'Your qualification has been updated successfully',
              severity: 'success',
            })
          );
        } catch (error) {
          const err = error as QueryErrorReturnValue;
          if (err.error.status !== 422) {
            dispatch(
              addSnackbar({
                message: err.error.message,
                severity: 'error',
              })
            );
          }
        }
      },
      invalidatesTags: ['EligibleModule'],
    }),
  }),
});

export const {
  useQualificationOptionsQuery,
  useLazyQualificationByEmailQuery,
  useQualificationByEmailQuery,
  useUpsertQualificationMutation,
} = qualificationApi;
